<template>
  <div class="search-filters__items-container">
    <idf-drawer
      height="100vh"
      :no-spacings="true"
      :show-close-button="false"
      :value="isFilterBlocVisibleOnMobile"
      :direction="isMobile ? IdfDrawerDirection.BTT : IdfDrawerDirection.RTL"
      @update:model-value="toggleFilterBlocOnMobile"
    >
      <template #header>
        <div class="search-filters__items-title">
          <span>{{ i18n._('Filtres') }}</span>
          <span
            class="adn-icon adn-icon--navigation-close search-filters__items-title-close"
            aria-label="Fermer"
            @click="toggleFilterBlocOnMobile"
          />
        </div>
      </template>
      <template #footer>
        <div class="search-filters__footer">
          <adn-button
            kind="tertiary"
            icon-position="left"
            class="search-results__reset-filter-button"
            @click.prevent="toggleFilterBlocOnMobile"
          >
            {{ i18n._('Annuler') }}
          </adn-button>
          <ais-current-refinements
            :excluded-attributes="hierarchicalAttributes"
            class="search-results__apply-filter-button"
          >
            <adn-button class="search-results__apply-filter-adn-button" @click.prevent="toggleFilterBlocOnMobile">
              <ais-stats>
                <template #default>{{ i18n._('Voir les résultats') }}</template>
              </ais-stats>
            </adn-button>
          </ais-current-refinements>
        </div>
      </template>
      <template #body>
        <ul class="search-filters__item-list">
          <ais-toggle-refinement
            :class-names="singleSelectClasses"
            class="search-filters__items-singleselect"
            data-test-discount-filter
            attribute="has_discount"
            label="Afficher exclusivement les produits remisés"
          >
            <template #default="{ value, refine }">
              <div
                class="search-filters__item-singleselect"
                :class="{
                  'search-filters__item-singleselect--selected': value.isRefined,
                }"
              >
                <span>{{ i18n._('Promotion') }}</span>
                <adn-checkbox
                  class="search-filters__item-checkbox"
                  :checked="value.isRefined"
                  @change="toggleFilter(refine, value)"
                />
              </div>
            </template>
          </ais-toggle-refinement>
          <ais-toggle-refinement
            v-show="currentCooperative.id"
            :class-names="singleSelectClasses"
            class="search-filters__items-singleselect"
            data-test-off-season-filter
            attribute="is_off_season"
            label="Afficher exclusivement les offres du moment"
          >
            <template #default="{ value, refine }">
              <div
                class="search-filters__item-singleselect"
                :class="{
                  'search-filters__item-singleselect--selected': value.isRefined,
                }"
              >
                <span>{{ i18n._('Offres du moment') }}</span>
                <adn-checkbox
                  class="search-filters__item-checkbox"
                  :checked="value.isRefined"
                  @change="toggleFilter(refine, value)"
                />
              </div>
            </template>
          </ais-toggle-refinement>
          <ais-toggle-refinement
            :class-names="singleSelectClasses"
            class="search-filters__items-singleselect"
            data-test-organic-filter
            attribute="organic"
            label="Afficher exclusivement les produits utilisables en agriculture biologique"
          >
            <template #default="{ value, refine }">
              <div
                class="search-filters__item-singleselect"
                :class="{
                  'search-filters__item-singleselect--selected': value.isRefined,
                }"
              >
                <span>{{ i18n._('Agriculture biologique') }}</span>
                <adn-checkbox
                  class="search-filters__item-checkbox"
                  :checked="value.isRefined"
                  @change="toggleFilter(refine, value)"
                />
              </div>
            </template>
          </ais-toggle-refinement>
          <ais-toggle-refinement
            v-show="currentCooperative.id"
            :class-names="singleSelectClasses"
            class="search-filters__items-singleselect"
            data-test-highlight-filter
            attribute="is_highlighted"
            :label="i18n._('Afficher exclusivement les produits recommandés par le partenaire')"
          >
            <template #default="{ value, refine }">
              <div
                class="search-filters__item-singleselect"
                :class="{
                  'search-filters__item-singleselect--selected': value.isRefined,
                }"
              >
                <span>
                  {{
                    currentCooperative && currentCooperative.name
                      ? i18n._('Recommandé par {name}', { name: currentCooperative.name })
                      : i18n._('Recommandé par le partenaire')
                  }}
                </span>
                <adn-checkbox
                  class="search-filters__item-checkbox"
                  :checked="value.isRefined"
                  @change="toggleFilter(refine, value)"
                />
              </div>
            </template>
          </ais-toggle-refinement>
          <ais-toggle-refinement
            v-for="(facet, index) in falsyFacets"
            :key="index"
            :class-names="singleSelectClasses"
            class="search-filters__items-singleselect"
            :attribute="facet.name"
            :label="facet.label"
          >
            <template #default="{ value, refine }">
              <div
                class="search-filters__item-singleselect"
                :class="{
                  'search-filters__item-singleselect--selected': value.isRefined,
                }"
              >
                <span>{{ facet.label }}</span>
                <adn-checkbox
                  class="search-filters__item-checkbox"
                  :checked="value.isRefined"
                  @change="toggleFilter(refine, value)"
                />
              </div>
            </template>
          </ais-toggle-refinement>
          <ais-refinement-list
            v-for="facet in orderedFacets"
            :key="facet.name"
            :class-names="{
              'ais-RefinementList--noRefinement': 'search-filters__items--noRefinement',
            }"
            :attribute="facet.name"
            searchable
            :limit="minimumFacetsToRetrieve"
            class="search-filters__multiselect"
            :sort-by="refinementListSortByAttributes"
          >
            <template #default="{ items, isFromSearch, refine }">
              <p-search-filter-family
                v-if="items.length > 0"
                :name="facet.label"
                :items="items"
                :is-from-search="isFromSearch"
                :refine="refine"
                @close-popin="toggleFilterBlocOnMobile"
                @change="sendTrackingEventClickFilter(facet.name)"
              />
            </template>
          </ais-refinement-list>
          <ais-menu-select attribute="supplier" :limit="0" style="display: none" />
        </ul>
      </template>
    </idf-drawer>
  </div>
</template>

<script lang="ts">
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import AdnCheckbox from '@b2ag/design-system/src/components/AdnCheckbox/AdnCheckbox.vue'
import IdfDrawer from '@b2ag/design-system/src/components/IdfDrawer/IdfDrawer.vue'
import { i18n } from '@b2ag/locale'
import PSearchFilterFamily from '../PSearchFilterFamily/PSearchFilterFamily.vue'
import { CUSTOM_FACETS, FACETS_LABELS, FALSY_FACETS } from './facets.setting'
import { DISJUNCTIVE_FACETS } from '../PAppSearch/constants'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import { defineComponent } from 'vue'
import {
  AisCurrentRefinements,
  AisMenuSelect,
  AisRefinementList,
  AisStats,
  AisToggleRefinement,
} from 'vue-instantsearch/vue3/es'
import { IdfDrawerDirection } from '@b2ag/design-system/src/components/IdfDrawer/IdfDrawer.model'

export default defineComponent({
  name: 'PSearchFilters',
  components: {
    AdnCheckbox,
    PSearchFilterFamily,
    IdfDrawer,
    AdnButton,
    AisToggleRefinement,
    AisStats,
    AisCurrentRefinements,
    AisRefinementList,
    AisMenuSelect,
  },
  props: {
    hierarchicalAttributes: {
      type: Array,
      default: () => [],
    },
    breadcrumbAttributes: {
      type: Array,
      default: () => [],
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    currentCooperative: {
      type: Object,
      default: () => ({}),
    },
    selectedFamilyName: {
      type: String,
      default: '',
    },
    navBarIsPresent: {
      type: Boolean,
      default: true,
    },
    filterBlocIsVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFilterBlocVisibleOnMobile: false,
      checkedFilterCount: 0,
      minimumFacetsToRetrieve: 500,
      refinementListSortByAttributes: ['isRefined', 'name:asc'],
      i18n,
    }
  },
  computed: {
    IdfDrawerDirection() {
      return IdfDrawerDirection
    },
    falsyFacets() {
      const keysFacets = DISJUNCTIVE_FACETS
      return keysFacets
        .filter((key) => key in FALSY_FACETS)
        .map((facet) => ({ label: FALSY_FACETS[facet], name: facet }))
    },
    orderedFacets() {
      const keysFacets = DISJUNCTIVE_FACETS
      return keysFacets
        .filter((key) => key in FACETS_LABELS)
        .filter((key) => !(key in CUSTOM_FACETS))
        .map((facet) => ({ label: FACETS_LABELS[facet], name: facet }))
    },
    singleSelectClasses() {
      return {
        'ais-ToggleRefinement--noRefinement': 'search-filters__items--noRefinement',
      }
    },
  },
  watch: {
    filterBlocIsVisible() {
      if (this.filterBlocIsVisible !== this.isFilterBlocVisibleOnMobile) {
        this.toggleFilterBlocOnMobile()
      }
    },
  },
  methods: {
    toggleFilterBlocOnMobile() {
      this.isFilterBlocVisibleOnMobile = !this.isFilterBlocVisibleOnMobile

      this.$emit('setMobileFiltersBlockDisplayed', this.isFilterBlocVisibleOnMobile)
    },
    toggleFilter(refine, value) {
      refine(value)
      this.sendTrackingEventClickFilter(value.name)
    },
    sendTrackingEventClickFilter(facetName) {
      const payload = {
        search_term: this.$route?.query?.query,
        click_filter: facetName,
      }
      useAnalyticService().trackEvent('page_search_results_click_filter', payload)
    },
  },
})
</script>

<style lang="scss" scoped>
.search-filters__items-container {
  :deep() {
    .idf-drawer__content--rtl {
      width: 40rem;
    }

    .idf-drawer__body {
      height: 100%;
    }
  }
}

.search-results__reset-filter-button {
  padding: 0 var(--spacing-3);
  margin-bottom: var(--spacing-2);
}

.search-results__apply-filter-button {
  flex: 1;
  margin-bottom: var(--spacing-2);
  width: 100%;
}

.search-results__apply-filter-adn-button {
  width: 100%;
}

.search-filters__items-title {
  font-size: var(--font-size-xl);
  font-weight: bold;
  padding-bottom: var(--spacing-3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #3c3c3c;
}

.search-filters__item-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding-top: var(--spacing-3);
  gap: var(--spacing-3);
  list-style-type: none;
  padding-right: var(--spacing-2);

  > li.search-filters__singleselect-wrapper {
    padding: var(--spacing-3) 0;
  }

  .search-filters__items-singleselect {
    gap: var(--spacing-3);
    display: flex;
    flex-direction: column;
    color: #3c3c3c;

    .search-filters__item-singleselect {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: bold;
      font-size: var(--font-size-l);

      .search-filters__item-checkbox {
        display: flex;
        justify-content: center;
      }

      &--selected {
        color: var(--color-main);
      }
    }
  }

  .search-filters__items--noRefinement {
    display: none;
  }

  div:not(.search-filters__items--noRefinement):first-child,
  div.search-filters__items--noRefinement + div:not(.search-filters__items--noRefinement) {
    border-top: none;
    padding-top: 0;
  }

  div:not(.search-filters__items--noRefinement):first-child ~ div,
  div.search-filters__items--noRefinement + div:not(.search-filters__items--noRefinement) ~ div {
    border-top: solid 1px #d5dce3;
    padding-top: var(--spacing-3);
  }
}

.search-filters__footer {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-3);
  width: 100%;
}
</style>
